import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSub } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown, Lightning, Sparkle, ArrowRight, Lock, Eye, EyeSlash, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICON = { free: Sparkle, pro: Lightning, elite: Crown };

function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-border bg-card p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2.5">
        <Lock size={18} className="text-volt" />
        <h2 className="font-heading text-lg font-semibold">Change Password</h2>
      </div>
      <form onSubmit={submit} className="mt-4 space-y-4 max-w-sm">
        <div>
          <Label className="label-xs">Current password</Label>
          <Input data-testid="current-password-input" type={show ? "text" : "password"} value={form.current_password} onChange={set("current_password")} required className="mt-1.5 bg-background rounded-sm mono" placeholder="••••••••" />
        </div>
        <div>
          <Label className="label-xs">New password</Label>
          <div className="relative mt-1.5">
            <Input data-testid="new-password-input" type={show ? "text" : "password"} value={form.new_password} onChange={set("new_password")} required className="bg-background rounded-sm mono pr-10" placeholder="At least 6 characters" />
            <button type="button" data-testid="toggle-new-password-visibility" onClick={() => setShow(!show)}
              aria-label={show ? "Hide password" : "Show password"}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors duration-200">
              {show ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <Label className="label-xs">Confirm new password</Label>
          <Input data-testid="confirm-password-input" type={show ? "text" : "password"} value={form.confirm} onChange={set("confirm")} required className="mt-1.5 bg-background rounded-sm mono" placeholder="••••••••" />
        </div>
        <Button data-testid="change-password-btn" type="submit" disabled={busy} className="bg-volt hover:bg-volt/90 text-white rounded-sm h-10">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}

function DeleteAccount() {
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Permanently delete your account and all data? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password });
      toast.success("Account deleted");
      setUser(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-red-500/40 bg-card p-6" data-testid="delete-account-card">
      <h2 className="font-heading text-lg font-semibold text-red-400">Delete Account</h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-md">
        Permanently removes your account, watchlist, alerts, signals, broker links and settings. This cannot be undone.
      </p>
      {!open ? (
        <Button data-testid="delete-account-open" variant="outline" onClick={() => setOpen(true)} className="mt-4 rounded-sm border-red-500/50 text-red-400">
          Delete my account
        </Button>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3 max-w-sm">
          <Label className="label-xs">Confirm with your password</Label>
          <Input data-testid="delete-account-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-background rounded-sm mono" />
          <Button data-testid="delete-account-btn" type="submit" disabled={busy} className="bg-red-600 hover:bg-red-600/90 text-white rounded-sm h-10">
            {busy ? "Deleting…" : "Permanently delete"}
          </Button>
        </form>
      )}
    </div>
  );
}

export default function Account() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { status, period, planTier, isAdmin, onTrial, trial, refresh } = useSub();
  const displayTier = isAdmin ? "elite" : (planTier || "free");
  const Icon = ICON[displayTier] || Sparkle;
  const trialEndsOn = trial?.ends_at
    ? new Date(trial.ends_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current period?")) return;
    try { await api.post("/billing/cancel"); toast.success("Subscription will cancel at period end"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl">
      <div className="label-xs">Account</div>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter">Your Plan</h1>

      <div className="mt-5 border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-volt/15 border border-volt/30">
              <Icon size={22} weight="fill" className={displayTier === "elite" ? "text-signal-yellow" : "text-volt"} />
            </div>
            <div>
              <div className="font-heading text-xl font-semibold capitalize" data-testid="current-tier">{displayTier}{isAdmin ? " (admin)" : ""}</div>
              <div className="mono text-xs text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <span className={`mono text-[11px] uppercase px-2 py-1 rounded-sm border ${status === "active" || status === "trialing" ? "text-signal-green border-signal-green/40 bg-signal-green/10" : "text-muted-foreground border-border"}`}>{status || "free"}</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 mono text-sm">
          <div><div className="label-xs">Billing</div><div className="mt-1">{period ? period : "—"}</div></div>
          <div><div className="label-xs">Status</div><div className="mt-1 capitalize">{status || "free"}</div></div>
        </div>

        <div className="mt-6 flex gap-3">
          {displayTier !== "elite" && (
            <Button data-testid="upgrade-btn" onClick={() => nav("/pricing")} className="bg-volt hover:bg-volt/90 rounded-sm gap-2">Upgrade <ArrowRight size={16} /></Button>
          )}
          {planTier !== "free" && !isAdmin && status !== "canceling" && status !== "canceled" && (
            <Button data-testid="cancel-sub-btn" onClick={cancel} variant="outline" className="rounded-sm border-border text-muted-foreground hover:text-signal-red">Cancel subscription</Button>
          )}
        </div>
      </div>

      {onTrial && (
        <div className="mt-5 border border-signal-green/30 bg-signal-green/[0.06] p-6" data-testid="trial-info-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-signal-green/15 border border-signal-green/30 shrink-0">
                <Clock size={22} weight="fill" className="text-signal-green" />
              </div>
              <div>
                <div className="font-heading text-lg font-semibold">Free trial — full access</div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  You have complete Elite-level access to every feature.
                </p>
                <p className="mono text-sm mt-2" data-testid="trial-ends-on">
                  Trial ends on <span className="text-signal-green font-semibold">{trialEndsOn || "—"}</span>
                  <span className="text-muted-foreground"> · {trial?.days_left} day{trial?.days_left === 1 ? "" : "s"} left</span>
                </p>
              </div>
            </div>
            <Button data-testid="trial-upgrade-btn" onClick={() => nav("/pricing")}
              className="bg-volt hover:bg-volt/90 text-white rounded-sm gap-2 shrink-0">
              Choose a plan <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      )}

      <ChangePassword />
      <DeleteAccount />
    </div>
  );
}
